import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

interface SignUpReferralStore {
  referenceSmsAccountId: string | null;
  signUpReference: string;
  setReferenceSmsAccountId: (referenceSmsAccountId: string | null) => void;
  setSignUpReference: (signUpReference: string) => void;
  clearSignUpReferral: () => void;
}

/**
 * Referral context (e.g. Dashpivot PQL banner, workspace switcher promo card)
 * captured from the URL and needed to survive a FusionAuth login round-trip.
 *
 * Backed by **sessionStorage** — not localStorage — so that:
 * - Stale referrals don't linger across browser sessions (closed tab → gone)
 * - Abandoned cross-account flows don't bleed into later /workspace-setup visits
 * - Parallel tabs don't share state
 *
 * Callers should call `clearSignUpReferral()` after any terminal outcome
 * (successful workspace creation, authoritative 403, routing the user away
 * as a non-member) to release the context eagerly rather than waiting for
 * the tab to close.
 */
export const useSignUpReferralStore = create<SignUpReferralStore>()(
  persist(
    (set) => ({
      referenceSmsAccountId: null,
      signUpReference: '',
      setReferenceSmsAccountId: (referenceSmsAccountId) => set(() => ({ referenceSmsAccountId })),
      setSignUpReference: (signUpReference) => set(() => ({ signUpReference })),
      clearSignUpReferral: () => set(() => ({ referenceSmsAccountId: null, signUpReference: '' })),
    }),
    {
      name: 'gb-signup-referral',
      storage: createJSONStorage(() => sessionStorage),
    },
  ),
);
